import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { listAPI, categoryAPI } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";
import ReactMarkdown from "react-markdown";

const Home = () => {
  const [lists, setLists] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("recent");
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search");

  usePageTitle(
    searchQuery ? `Search: ${searchQuery}` : "Guess. Reveal. Complete."
  );

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadLists();
  }, [sortBy, searchQuery]);

  const loadCategories = async () => {
    try {
      const response = await categoryAPI.getAllCategories();
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const loadLists = async () => {
    setLoading(true);
    try {
      let response;
      if (searchQuery) {
        response = await listAPI.searchLists(searchQuery);
      } else {
        response = await listAPI.getPublicLists(0, 20, sortBy);
      }
      setLists(response.data.content);
    } catch (error) {
      console.error("Failed to load lists:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
          {searchQuery
            ? `Search Results for "${searchQuery}"`
            : "Explore Lists"}
        </h1>
        <p className="text-gray-400 text-sm sm:text-base">
          Choose a list and test your knowledge by guessing all the items!
        </p>
      </div>

      {/* Categories */}
      {!searchQuery && (
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4">
            Categories
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="bg-dark-card hover:bg-dark-hover border border-gray-700 p-3 sm:p-4 rounded-lg text-center transition active:scale-95"
              >
                <h3 className="text-white font-semibold text-sm sm:text-base truncate">
                  {category.name}
                </h3>
                <p className="text-gray-400 text-xs sm:text-sm">
                  {category.listCount}{" "}
                  {category.listCount === 1 ? "list" : "lists"}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Sort Options */}
      {!searchQuery && (
        <div className="mb-4 sm:mb-6 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-semibold text-white">
            All Lists
          </h2>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 text-sm sm:text-base bg-dark-card border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="recent">Recently Added</option>
            <option value="popularity">Most Popular</option>
            <option value="most-items">Most Items</option>
            <option value="fewest-items">Fewest Items</option>
            <option value="az">A → Z</option>
            <option value="za">Z → A</option>
          </select>
        </div>
      )}

      {/* Lists Grid */}
      {loading ? (
        <div className="text-center text-gray-400 py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="mt-4">Loading...</p>
        </div>
      ) : lists.length === 0 ? (
        <div className="text-center text-gray-400 py-12 bg-dark-card border border-gray-700 rounded-lg">
          <p className="text-lg">
            {searchQuery
              ? "No lists found for your search."
              : "No lists available."}
          </p>
          {searchQuery && (
            <button
              onClick={() => (window.location.href = "/")}
              className="mt-4 text-blue-400 hover:text-blue-300 underline"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {lists.map((list) => (
            <Link
              key={list.id}
              to={`/list/${list.slug}`}
              className="bg-dark-card hover:bg-dark-hover border border-gray-700 p-4 sm:p-6 rounded-lg transition group active:scale-98"
            >
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition line-clamp-2">
                {list.title}
              </h3>

              {list.description && (
                <div className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2 prose prose-invert prose-sm">
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => <span {...props} />,
                      strong: ({ node, ...props }) => (
                        <strong className="text-white" {...props} />
                      ),
                      em: ({ node, ...props }) => <em {...props} />,
                    }}
                  >
                    {list.description}
                  </ReactMarkdown>
                </div>
              )}

              <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                {list.categories
                  .filter((cat) => cat.name.toLowerCase() !== "ranked")
                  .map((cat) => (
                    <span
                      key={cat.id}
                      className="px-2 py-0.5 sm:py-1 bg-blue-600/20 text-blue-400 text-xs rounded whitespace-nowrap"
                    >
                      {cat.name}
                    </span>
                  ))}
                {list.isRanked && (
                  <span className="px-2 py-0.5 sm:py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded whitespace-nowrap">
                    Ranked
                  </span>
                )}
              </div>

              <div className="flex justify-between items-center text-xs sm:text-sm text-gray-400 mb-2">
                <span className="font-medium">{list.totalItems} items</span>
                <div className="flex items-center gap-1.5">
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  <span>
                    {list.popularity} {list.popularity === 1 ? "play" : "plays"}
                  </span>
                </div>
              </div>

              {/* Progress Indicator */}
              {list.isCompleted && (
                <div className="flex items-center gap-1.5 text-green-400 text-xs sm:text-sm font-medium">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>Completed</span>
                </div>
              )}
              {!list.isCompleted && list.isStarted && (
                <div className="flex items-center gap-1.5 text-orange-400 text-xs sm:text-sm font-medium">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    In Progress ({list.guessedCount}/{list.totalItems})
                  </span>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Home;
